import React, { useEffect, useState, useRef } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import axios from './utils/authInterceptor';
import './App.css';
import './components/components.css';
import TokenDisplay from './components/tokens/TokenDisplay';
import TokenWalletModal from './components/tokens/TokenWalletModal';

// Components
import Dashboard from './components/Dashboard';
import TutorInterface from './components/TutorInterface';
import AITrainer from './components/AITrainer';
import MorpheusInterface from './components/MorpheusInterface';
// Free-forever teacher tier (docs/FREE_TEACHER_TIER_SCOPE.md)
import TeacherOnboarding from './components/TeacherOnboarding';
import TeacherLiteDashboard from './components/TeacherLiteDashboard';
import StudentJoinPage from './components/StudentJoinPage';
import TeacherShare from './components/TeacherShare';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import ParentDashboard from './components/ParentDashboardNew';
import ParentInformation from './components/ParentInformation';
import GuestTutorExperience from './components/GuestTutorExperience';
import GuestTutorPage from './components/GuestTutorPage';
import FeatureTiers from './components/FeatureTiers';
import PricingTiers from './components/PricingTiers';
import UpgradePage from './components/UpgradePage';
import SubscriptionManagement from './components/SubscriptionManagement';
import PaymentSuccess from './components/PaymentSuccess';
import PaymentCancel from './components/PaymentCancel';
import EmailVerification from './components/EmailVerification';
import VisualizationController from './components/VisualizationController';
import Research from './components/Research';
import Accessibility from './components/Accessibility';
import Branding from './components/Branding';
import FriendTraining from './components/FriendTraining';
import OneClickNeoTrainer from './components/OneClickNeoTrainer';
import SimpleNeoTrainer from './components/SimpleNeoTrainer';
import BountyMarketplace from './components/BountyMarketplace';
import GuestHelpInterface from './components/GuestHelpInterface';
import FriendCollaborationInterface from './components/FriendCollaborationInterface';
import WelcomeHelper from './components/WelcomeHelper';
import NewsletterLanding from './components/NewsletterLanding';
import AILearningLabLanding from './components/AILearningLabLanding';
import CodeEntryLanding from './components/CodeEntryLanding';
import GroupManagement from './components/GroupManagement';
import SchoolAdminDashboard from './components/SchoolAdminDashboard';
import DistrictAdminDashboard from './components/DistrictAdminDashboard';
import SystemAdminDashboard from './components/SystemAdminDashboard';
import MetroMinistriesDashboard from './components/MetroMinistriesDashboard';
import InvitationSignup from './components/InvitationSignup';

import SponsorPage from './components/SponsorPage';

// /signup router: role-tagged arrivals (?role=teacher|student|parent) go to
// Login, which handles role pre-selection + signup mode + FERPA attestation
// (FREE_TEACHER_TIER_SCOPE.md Day 1). Everything else keeps the existing
// invitation-code signup.
const SignupRouter = () => {
  const location = useLocation();
  const role = new URLSearchParams(location.search).get('role');
  if (role && ['student', 'teacher', 'parent'].includes(role)) {
    return <Navigate to={`/login${location.search}`} replace />;
  }
  return <InvitationSignup />;
};

// CSRF Token Helper
const getCSRFToken = () => {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
};

// Configure axios to send cookies with all requests (required for JWT authentication)
axios.defaults.withCredentials = true;

// Setup axios interceptor for CSRF tokens
axios.interceptors.request.use((config) => {
  // Only add CSRF token for POST, PUT, DELETE, PATCH requests
  if (['post', 'put', 'delete', 'patch'].includes(config.method?.toLowerCase())) {
    const csrfToken = getCSRFToken();
    if (csrfToken) {
      config.headers['X-CSRF-Token'] = csrfToken;
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// VisualizationTester has been removed as part of the visualization system migration
// Using existing VoiceInput component integrated with TutorInterface

function App() {
  const location = useLocation();
  const [apiStatus, setApiStatus] = useState({
    status: 'loading',
    morpheus: false,
    neo: false
  });
  const [userRole, setUserRole] = useState(null); // null, 'student', 'teacher', or 'administrator'
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [userTier, setUserTier] = useState('guest'); // Default to guest tier
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0); // Token balance for badge
  const [tokenAnimation, setTokenAnimation] = useState({ show: false, amount: 0, fromX: 0, fromY: 0 }); // Flying animation
  const [showTokenWallet, setShowTokenWallet] = useState(false); // Token wallet modal
  const profileDropdownRef = useRef(null);

  // Day 5 — capture inbound viral attribution. Any path with ?ref=<id>
  // sets a 30-day tappy_ref cookie that the signup endpoint reads to
  // populate users.referred_by_user_id. Logs the visit once per cookie
  // lifetime so we can compute funnel rates without dedupe noise.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref && /^\d+$/.test(ref)) {
        const existing = document.cookie
          .split('; ')
          .find((row) => row.startsWith('tappy_ref='));
        if (!existing) {
          // 30-day attribution window. SameSite=Lax so links from email/SMS
          // still pass attribution; Secure when on https.
          const isHttps = window.location.protocol === 'https:';
          document.cookie =
            `tappy_ref=${ref}; path=/; max-age=${60 * 60 * 24 * 30}; ` +
            `SameSite=Lax${isHttps ? '; Secure' : ''}`;
          fetch('/api/teacher/referrals/log-visit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              inviter_user_id: parseInt(ref, 10),
              share_token: params.get('share_token') || null,
              landing_path: window.location.pathname,
              referrer: document.referrer || null,
            }),
          }).catch(() => {});
        }
      }
    } catch (e) {
      // Attribution must never block app load
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleDropdownAction = (action) => {
    setIsProfileDropdownOpen(false);
    
    switch(action) {
      case 'pricing':
        window.location.href = '/app/pricing';  // Authenticated pricing route
        break;
      case 'account':
        window.location.href = '/subscription';
        break;
      case 'trainer':
        window.location.href = '/trainer';
        break;
      case 'ai-tutor':
        window.location.href = '/neo-trainer';
        break;
      case 'friends':
        window.location.href = '/friends';
        break;
      case 'parent':
        window.location.href = '/parent';
        break;
      case 'research':
        window.location.href = '/app/research';  // Authenticated research route
        break;
      case 'accessibility':
        window.location.href = '/app/accessibility';  // Authenticated accessibility route
        break;
      case 'family':
        window.location.href = '/family';
        break;
      case 'logout':
        handleLogout();
        break;
      default:
        // Handle unknown actions gracefully
        console.warn('Unknown dropdown action:', action);
        break;
    }
  };
  
  // Helper function to check feature access
  const hasFeatureAccess = (featureName) => {
    return FeatureTiers.hasAccess(featureName, user);
  };

  // Check authentication on startup - now using HttpOnly cookies
  useEffect(() => {
    // With HttpOnly cookies, we don't need to manually set the token
    // as it will be sent automatically with the request
      
    // Verify token with backend
    axios.get('/api/auth/verify')
      .then(response => {
        if (response.data.valid) {
          setUser(response.data.user);
          setUserRole(response.data.user.role);
          setIsAuthenticated(true);
          // Set the user tier based on the user data
          setUserTier(FeatureTiers.getUserTier(response.data.user));
          
          // Save user info to localStorage for client-side access
          localStorage.setItem('user_info', JSON.stringify(response.data.user));
        } else {
          // Invalid token, clean up local storage
          localStorage.removeItem('user_info');
          setIsAuthenticated(false);
          setUserTier('guest'); // Reset to guest tier
        }
        setIsVerifying(false);
      })
      .catch(error => {
        console.error('Token verification error:', error);
        // On error, clean up local storage
        localStorage.removeItem('user_info');
        setIsAuthenticated(false);
        setUserTier('guest'); // Reset to guest tier
        setIsVerifying(false);
      });
  }, []);
  
  // Check for temporary subscription tier data from payment flow
  useEffect(() => {
    if (user) {
      // Check first for complete subscription data
      const subscriptionDataStr = localStorage.getItem('latest_subscription_data');
      if (subscriptionDataStr) {
        try {
          const subscriptionData = JSON.parse(subscriptionDataStr);
          console.log('Found complete subscription data in localStorage:', subscriptionData);
          
          // Create updated user object with the complete subscription information
          const updatedUser = {
            ...user,
            // If the user's role is still 'registered', update it to match the tier
            role: user.role === 'registered' ? subscriptionData.tier : user.role,
            subscription: {
              ...(user.subscription || {}),
              ...subscriptionData
            }
          };
          
          // Update application state with the new user information
          setUser(updatedUser);
          const newTier = FeatureTiers.getUserTier(updatedUser);
          setUserTier(newTier);
          
          // Save the complete updated user info to localStorage
          localStorage.setItem('user_info', JSON.stringify(updatedUser));
          
          // Remove the temporary storage now that we've used it
          localStorage.removeItem('latest_subscription_data');
          localStorage.removeItem('latest_subscription_tier');
        } catch (e) {
          console.error('Error parsing subscription data from localStorage:', e);
        }
      } else {
        // Fall back to the simple tier-only approach if full data isn't available
        const latestTier = localStorage.getItem('latest_subscription_tier');
        if (latestTier) {
          console.log('Found basic tier data in localStorage:', latestTier);
          
          // Create updated user object with the tier information
          const updatedUser = {
            ...user,
            // If the user's role is still 'registered', update it to match the tier
            role: user.role === 'registered' ? latestTier : user.role,
            subscription: {
              ...(user.subscription || {}),
              tier: latestTier,
              status: 'active'
            }
          };
          
          // Update application state with the new user information
          setUser(updatedUser);
          const newTier = FeatureTiers.getUserTier(updatedUser);
          setUserTier(newTier);
          
          // Save the complete updated user info to localStorage
          localStorage.setItem('user_info', JSON.stringify(updatedUser));
          
          // Remove the temporary storage now that we've used it
          localStorage.removeItem('latest_subscription_tier');
        }
      }
    }
  }, [user?.id]); // Only depend on user.id to prevent infinite loop

  useEffect(() => {
    // Check API health on component mount
    axios.get('/api/health')
      .then(response => {
        setApiStatus({
          status: 'connected',
          morpheus: response.data.models.morpheus,
          neo: response.data.models.neo
        });
      })
      .catch(error => {
        console.error('API Health Check Error:', error);
        setApiStatus({
          status: 'error',
          morpheus: false,
          neo: false
        });
      });
  }, []);

  // Add logout event listener for automatic token refresh system
  useEffect(() => {
    const handleAuthLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      setUserRole('guest');
      setUserTier('guest');
      
      // Don't redirect to login for friend collaboration paths - these should work for guests
      const isFriendCollaborationPath = window.location.pathname.startsWith('/join/');
      
      // Only redirect to login if not already on login page and not on friend collaboration
      if (window.location.pathname !== '/login' && !isFriendCollaborationPath) {
        // If on main domain, redirect to app subdomain for login
        if (window.location.hostname === 'teachtappy.ai' || window.location.hostname === 'www.teachtappy.ai') {
          window.location.href = 'https://app.teachtappy.ai/login';
        } else {
          window.location.href = '/login';
        }
      }
    };

    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  // Utility function to get CSRF token from cookies
  const getCSRFToken = () => {
    const name = 'csrf_token=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return null;
  };

  // Proactive token refresh - refresh tokens before they expire
  useEffect(() => {
    if (isAuthenticated) {
      const refreshInterval = setInterval(async () => {
        try {
          // Include CSRF token for secure refresh
          const csrfToken = getCSRFToken();
          const headers = csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
          
          await axios.post('/api/auth/refresh', {}, { headers });
          console.log('Token refreshed proactively');
        } catch (error) {
          console.log('Proactive refresh failed, user may need to login');
          // Don't logout on proactive refresh failure - let interceptor handle it
        }
      }, 3.5 * 60 * 60 * 1000); // Every 3.5 hours (30min before 4hr expiry)

      return () => clearInterval(refreshInterval);
    }
  }, [isAuthenticated]);

  // Fetch token balance for badge display (only for users who have tokens)
  useEffect(() => {
    if (isAuthenticated && userRole) {
      // Only fetch token balance for students (admins/teachers don't have token balances)
      const rolesWithTokens = ['student', 'guest', 'registered'];
      if (rolesWithTokens.includes(userRole)) {
        const fetchTokenBalance = async () => {
          try {
            const response = await axios.get('/api/tokens/balance');
            setTokenBalance(response.data.balance || 0);
          } catch (error) {
            console.error('Error fetching token balance for badge:', error);
          }
        };
        fetchTokenBalance();
      }
    }
  }, [isAuthenticated, userRole]);

  // Global token animation trigger function
  const triggerTokenAnimation = (amount, sourceElement) => {
    if (sourceElement) {
      const rect = sourceElement.getBoundingClientRect();
      setTokenAnimation({
        show: true,
        amount: amount,
        fromX: rect.left + rect.width / 2,
        fromY: rect.top + rect.height / 2
      });
      
      // Update token balance immediately for instant feedback
      setTokenBalance(prev => prev + amount);

      // Also refresh from server to ensure accuracy (only for roles with tokens)
      setTimeout(async () => {
        const rolesWithTokens = ['student', 'guest', 'registered'];
        if (rolesWithTokens.includes(userRole)) {
          try {
            const response = await axios.get('/api/tokens/balance');
            setTokenBalance(response.data.balance || 0);
          } catch (error) {
            console.error('Error refreshing token balance:', error);
          }
        }
      }, 500);
      
      // Hide animation after 2 seconds
      setTimeout(() => {
        setTokenAnimation({ show: false, amount: 0, fromX: 0, fromY: 0 });
      }, 2000);
    }
  };

  // Global function to refresh token balance (for other components to call)
  const refreshTokenBalance = async () => {
    if (isAuthenticated && userRole) {
      const rolesWithTokens = ['student', 'guest', 'registered'];
      if (rolesWithTokens.includes(userRole)) {
        try {
          const response = await axios.get('/api/tokens/balance');
          setTokenBalance(response.data.balance || 0);
        } catch (error) {
          console.error('Error refreshing token balance:', error);
        }
      }
    }
  };

  // Make token functions globally available
  useEffect(() => {
    window.triggerTokenAnimation = triggerTokenAnimation;
    window.refreshTokenBalance = refreshTokenBalance;
    return () => {
      window.triggerTokenAnimation = null;
      window.refreshTokenBalance = null;
    };
  }, [isAuthenticated]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setUserRole(userData.role);
    setIsAuthenticated(true);
    setUserTier(FeatureTiers.getUserTier(userData)); // Update user tier based on user data
  };

  const handleLogout = () => {
    // Call logout API - the server will clear the HttpOnly cookie
    axios.post('/api/auth/logout')
      .then(() => {
        // Clean up local storage (cookie will be cleared by the server)
        localStorage.removeItem('user_info');
        setUser(null);
        setUserRole(null);
        setIsAuthenticated(false);
        setUserTier('guest'); // Reset to guest tier
        
        // Wait for cookies to be processed, then redirect to login page
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      })
      .catch(error => {
        console.error('Logout error:', error);
        // Clean up anyway in case of error
        localStorage.removeItem('user_info');
        setUser(null);
        setUserRole(null);
        setIsAuthenticated(false);
        setUserTier('guest'); // Reset to guest tier
        
        // Wait for cookies to be processed, then redirect to login page
        setTimeout(() => {
          window.location.href = '/login';
        }, 100);
      });
  };

  // Show loading state while verifying authentication
  if (isVerifying) {
    return (
      <div className="app">
        <div className="loading-container">
          <img 
            src="/images/TAPPY-logo-color.png" 
            alt="Tappy Tutor Logo" 
            style={{
              height: '60px',
              width: 'auto',
              marginBottom: '1.5rem',
              objectFit: 'contain'
            }}
          />
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Check if we're on the main domain (not app subdomain)
  const isMainDomain = () => {
    // Skip this check in development mode
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return false;
    }
    // Check if we're on the main domain (not a subdomain)
    return window.location.hostname.split('.').length === 2 || 
           (window.location.hostname.startsWith('www.') && window.location.hostname.split('.').length === 3);
  };

  // If on main domain, only show guest experience
  if (isMainDomain()) {
    console.log('Main domain routing, current path:', window.location.pathname);
    return (
      <Routes>
        <Route path="/" element={
          <VisualizationController>
            <GuestTutorExperience userTier={userTier} hasFeatureAccess={hasFeatureAccess} />
          </VisualizationController>
        } />
        <Route path="/research" element={
          <Research onActivateVoice={() => window.location.href='/'} />
        } />
        <Route path="/for-parents" element={
          <ParentInformation onActivateVoice={() => window.location.href='/tutor'} />
        } />
        <Route path="/pricing" element={<PricingTiers userTier={userTier} currentUser={user} />} />
        <Route path="/sponsor" element={<SponsorPage />} />
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/newsletter" element={<NewsletterLanding />} />
        <Route path="/learning-lab" element={<AILearningLabLanding />} />
        <Route path="/guest-tutor" element={<Navigate to="/tutor" replace />} />
        <Route path="/join" element={<CodeEntryLanding />} />
        <Route path="/join/:invite_token" element={<FriendCollaborationInterface />} />
        <Route path="/class-join" element={<StudentJoinPage />} />
        <Route path="/welcome-helper" element={<WelcomeHelper />} />
        <Route path="/accept-invite" element={<InvitationSignup />} />
        <Route path="/signup" element={<SignupRouter />} />
        {/* Visualization tester route removed */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  // Determine what to render based on authentication state
  if (!isAuthenticated) {
    // Don't show App.js header for /tutor - TutorInterface has its own fixed header
    const showGuestHeader = false;

    return (
      <div className="app">
        {showGuestHeader && (
          <header style={Branding.components.cleanHeader.container}>
            {/* Logo Section */}
            <div style={Branding.components.cleanHeader.logoSection}>
              <img
                src="/images/TAPPY-logo-white.png"
                alt="Tappy Tutor"
                style={Branding.components.cleanHeader.logo}
              />
            </div>

            {/* Center Navigation */}
            <nav style={Branding.components.cleanHeader.navigation}>
              <Link
                to="/tutor"
                style={Branding.helpers.createNavLink(window.location.pathname === '/tutor')}
              >
                Voice Tutor
              </Link>
            </nav>

            {/* Right side - Login/Signup */}
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <Link
                to="/login"
                style={{
                  backgroundColor: 'transparent',
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  padding: '8px 12px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                Log In
              </Link>
              <span style={{ color: 'rgba(255, 255, 255, 0.5)' }}>|</span>
              <Link
                to="/pricing"
                style={{
                  backgroundColor: 'transparent',
                  color: 'rgba(255, 255, 255, 0.8)',
                  textDecoration: 'none',
                  padding: '8px 12px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'all 0.2s ease',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  e.target.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = 'rgba(255, 255, 255, 0.8)';
                }}
              >
                Sign Up
              </Link>
            </div>
          </header>
        )}

        <main className="app-content">
          <Routes>
            <Route path="/" element={
              <VisualizationController>
                <GuestTutorExperience userTier={userTier} hasFeatureAccess={hasFeatureAccess} />
              </VisualizationController>
            } />
            <Route path="/tutor" element={
              <VisualizationController>
                <TutorInterface userTier="guest" hasFeatureAccess={hasFeatureAccess} user={null} />
              </VisualizationController>
            } />
            <Route path="/dashboard" element={<Navigate to="/tutor" replace />} />
            <Route path="/research" element={
          <Research onActivateVoice={() => window.location.href='/'} />
        } />
        <Route path="/for-parents" element={
          <ParentInformation onActivateVoice={() => window.location.href='/tutor'} />
        } />
        <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pricing" element={<PricingTiers userTier={userTier} currentUser={user} />} />
        <Route path="/sponsor" element={<SponsorPage />} />
        <Route path="/upgrade" element={<UpgradePage user={user} userTier={userTier} />} />
        <Route path="/payment/success" element={<PaymentSuccess onRefreshUser={handleLoginSuccess} />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/verify-email" element={<EmailVerification />} />
        <Route path="/newsletter" element={<NewsletterLanding />} />
        <Route path="/learning-lab" element={<AILearningLabLanding />} />
        <Route path="/guest-tutor" element={<Navigate to="/tutor" replace />} />
        <Route path="/help/:userId/:sessionId" element={<GuestHelpInterface />} />
        <Route path="/join" element={<CodeEntryLanding />} />
        <Route path="/join/:invite_token" element={<FriendCollaborationInterface />} />
        <Route path="/class-join" element={<StudentJoinPage />} />
            <Route path="/welcome-helper" element={<WelcomeHelper />} />
            <Route path="/accept-invite" element={<InvitationSignup />} />
            <Route path="/signup" element={<SignupRouter />} />
            {/* Visualization tester route removed */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    );
  }

  // Special case for email verification - always render this regardless of auth status
  if (window.location.pathname === '/verify-email') {
    return <EmailVerification />;
  }

  // If teacher or admin UI is requested, use proper routing
  if (userRole === 'teacher' || userRole === 'administrator') {
    // Free-forever teacher tier: no school_license_id → serve the lite
    // dashboard + onboarding flow instead of MorpheusInterface (which
    // implicitly requires a school license). School-licensed teachers
    // and admins continue to the full MorpheusInterface unchanged.
    const isFreeTierTeacher = (
      userRole === 'teacher'
      && !user?.school_license_id
    );

    if (isFreeTierTeacher) {
      return (
        <Routes>
          <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />
          <Route path="/teacher/onboarding" element={<TeacherOnboarding user={user} />} />
          <Route path="/teacher/dashboard" element={<TeacherLiteDashboard user={user} onLogout={handleLogout} />} />
          <Route path="/teacher/share" element={<TeacherShare user={user} />} />
          <Route path="/upgrade" element={<UpgradePage user={user} />} />
          <Route path="*" element={<Navigate to="/teacher/dashboard" replace />} />
        </Routes>
      );
    }

    return (
      <Routes>
        <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />
        <Route path="/teacher/*" element={
          <MorpheusInterface
            user={user}
            onLogout={handleLogout}
            isAdmin={userRole === 'administrator'}
            userTier={userTier}
            hasFeatureAccess={hasFeatureAccess}
          />
        } />
        <Route path="*" element={<Navigate to="/teacher/dashboard" replace />} />
      </Routes>
    );
  }

  // Nonprofit Admin Dashboard routing (Metro Ministries / community org)
  if (userRole === 'nonprofit_admin') {
    const schoolId = user?.school_license_id;
    if (!schoolId) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h1>Program Setup Required</h1>
          <p>Your account has not been assigned to a program license.</p>
          <p>Please contact support at care@teachtappy.com</p>
          <button onClick={handleLogout} style={{ marginTop: '20px', padding: '10px 20px' }}>Logout</button>
        </div>
      );
    }
    return (
      <Routes>
        <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />
        <Route path="/admin/program/*" element={
          <MetroMinistriesDashboard user={user} onLogout={handleLogout} />
        } />
        <Route path="*" element={<Navigate to="/admin/program/overview" replace />} />
      </Routes>
    );
  }

  // School Admin Dashboard routing (Session-Based - Secure)
  if (userRole === 'school_admin') {
    // School admin has a single school (user.school_license_id)
    // School ID is not in URL - determined by session (more secure)
    const schoolId = user?.school_license_id;

    if (!schoolId) {
      return (
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <h1>School Admin Setup Required</h1>
          <p>Your account has not been assigned to a school license.</p>
          <p>Please contact support at care@teachtappy.com</p>
          <button onClick={handleLogout} style={{ marginTop: '20px', padding: '10px 20px' }}>
            Logout
          </button>
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />
        {/* Session-based routing - no school ID in URL */}
        <Route path="/admin/school/*" element={
          <SchoolAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />
        <Route path="*" element={<Navigate to="/admin/school/overview" replace />} />
      </Routes>
    );
  }

  // District Admin Dashboard routing
  if (userRole === 'district_admin') {
    return (
      <Routes>
        <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />

        {/* District overview - shows all schools */}
        <Route path="/admin/district/*" element={
          <DistrictAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />

        {/* Individual school view - district admin can view any school in their district */}
        <Route path="/admin/schools/:schoolId/*" element={
          <SchoolAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />

        <Route path="*" element={<Navigate to="/admin/district/overview" replace />} />
      </Routes>
    );
  }

  // System Admin Dashboard routing (platform operators)
  if (userRole === 'system_admin') {
    return (
      <Routes>
        <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />

        {/* System admin dashboard - investor metrics & platform overview */}
        <Route path="/admin/system/*" element={
          <SystemAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />

        {/* Can also view any school */}
        <Route path="/admin/schools/:schoolId/*" element={
          <SchoolAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />

        {/* Can also view district dashboards */}
        <Route path="/admin/district/*" element={
          <DistrictAdminDashboard
            user={user}
            onLogout={handleLogout}
          />
        } />

        <Route path="*" element={<Navigate to="/admin/system/overview" replace />} />
      </Routes>
    );
  }

  // Parent Portal routing - render outside the main wrapper (no purple header)
  // Includes both /parent (dashboard) and /family (family management) routes
  if (window.location.pathname === '/parent' || window.location.pathname.startsWith('/parent/') || window.location.pathname === '/family') {
    return (
      <ParentDashboard
        user={user}
        onLogout={handleLogout}
        userTier={userTier}
        hasFeatureAccess={hasFeatureAccess}
      />
    );
  }

  // Subscription Management - render outside the main wrapper (no purple header)
  if (window.location.pathname === '/subscription') {
    return (
      <SubscriptionManagement
        user={user}
        onBack={() => window.history.back()}
      />
    );
  }

  // Otherwise render the student UI
  // Hide App.js header on /tutor route - TutorInterface has its own header
  const hideAppHeader = window.location.pathname === '/tutor' || window.location.pathname === '/';

  return (
    <div className="app">
      {!hideAppHeader && (
      <header style={Branding.components.cleanHeader.container}>
        {/* Logo Section */}
        <div style={Branding.components.cleanHeader.logoSection}>
          <img 
            src="/images/TAPPY-logo-white.png" 
            alt="Tappy Tutor" 
            style={Branding.components.cleanHeader.logo}
          />
        </div>
        
        {/* Center Navigation */}
        <nav style={Branding.components.cleanHeader.navigation}>
          <Link
            to="/tutor"
            style={Branding.helpers.createNavLink(window.location.pathname === '/tutor')}
          >
            Voice Tutor
          </Link>
        </nav>

        {/* Profile Section */}
        <div style={{...Branding.components.cleanHeader.profileSection, position: 'relative'}} ref={profileDropdownRef}>
          <button
            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
            style={Branding.components.cleanHeader.profileButton}
            onMouseEnter={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            {/* Status indicator */}
            <span 
              title={apiStatus.status === 'loading' ? 'Connecting...' : 
                    apiStatus.status === 'connected' ? 'Connected' : 'Connection Error'}
              style={Branding.helpers.createStatusIndicator(apiStatus.status === 'connected')}
            />
            
            {/* User initial */}
            <span style={Branding.components.cleanHeader.userAvatar}>
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </span>
          </button>

          {/* Smart Token Badge - iPhone notification style */}
          {isAuthenticated && tokenBalance > 0 && (
            <div 
              onClick={(e) => {
                e.stopPropagation();
                setShowTokenWallet(true);
              }}
              style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: 'linear-gradient(135deg, #F59E0B, #F97316)',
                color: 'white',
                borderRadius: '12px',
                padding: '2px 6px',
                fontSize: '11px',
                fontWeight: '700',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4), 0 0 0 2px rgba(255, 255, 255, 0.3)',
                minWidth: '20px',
                textAlign: 'center',
                letterSpacing: '-0.02em',
                animation: tokenBalance > 0 ? 'tokenBadgePulse 0.6s ease-out' : 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                zIndex: 10
              }}
              title={`${tokenBalance} tokens earned - Click to view wallet`}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              {tokenBalance > 999 ? '999+' : tokenBalance}
            </div>
          )}

          {/* Dropdown Menu */}
          {isProfileDropdownOpen && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: '0',
              marginTop: '0.5rem',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              minWidth: '250px',
              zIndex: 1000,
              padding: '1rem 0',
              fontSize: '0.9rem'
            }}>
              {/* User Info Header */}
              <div style={{
                padding: '0 1rem 0.75rem 1rem',
                borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                marginBottom: '0.5rem'
              }}>
                <div style={{ fontWeight: '600', color: '#1a1a1a' }}>
                  {user?.username || 'User'}
                </div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>
                  {userTier === 'guest' ? 'Guest' : userTier.charAt(0).toUpperCase() + userTier.slice(1)} Plan
                </div>
              </div>

              {/* Menu Items */}
              <button
                onClick={() => handleDropdownAction('pricing')}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#333',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                📊 View Pricing & Plans
              </button>

              {userTier !== 'guest' && (
                <button
                  onClick={() => handleDropdownAction('account')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  ⚙️ Account Settings
                </button>
              )}

              {hasFeatureAccess('MARKETPLACE_ACCESS') && (
                <button
                  onClick={() => handleDropdownAction('trainer')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  🤖 AI Trainer
                </button>
              )}

              {hasFeatureAccess('MARKETPLACE_ACCESS') && (
                <button
                  onClick={() => handleDropdownAction('ai-tutor')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  🤖 AI Tutor
                </button>
              )}

              {hasFeatureAccess('FRIEND_TRAINING_HOST') && (
                <button
                  onClick={() => handleDropdownAction('friends')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  👥 Friends
                </button>
              )}

              {hasFeatureAccess('LEARNING_PORTFOLIO') && (
                <button
                  onClick={() => handleDropdownAction('parent')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  👨‍👩‍👧‍👦 Parent View
                </button>
              )}

              <button
                onClick={() => handleDropdownAction('accessibility')}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#333',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ⚙️ Accessibility
              </button>

              <button
                onClick={() => handleDropdownAction('research')}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#333',
                  fontSize: '0.9rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                🔬 Research
              </button>

              {(userTier === 'home' || userTier === 'expert-family' || hasFeatureAccess('GROUP_MANAGEMENT')) && (
                <button
                  onClick={() => handleDropdownAction('family')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    background: 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    color: '#333',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(0, 0, 0, 0.05)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  👥 Family & Groups
                </button>
              )}

              {/* Divider */}
              <div style={{
                height: '1px',
                background: 'rgba(0, 0, 0, 0.1)',
                margin: '0.5rem 0'
              }} />

              <button
                onClick={() => handleDropdownAction('logout')}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  border: 'none',
                  background: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: '#dc2626',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(220, 38, 38, 0.05)'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>
      </header>
      )}

      <main className="app-content">
        <Routes>
          {/* Root path redirects to tutor for immediate engagement - best practice for conversion */}
          <Route path="/" element={<Navigate to="/tutor" replace />} />

          {/* Dashboard redirects to tutor - parents will have separate login/dashboard later */}
          <Route path="/dashboard" element={<Navigate to="/tutor" replace />} />
          
          {/* Bounty marketplace route */}
          <Route path="/bounties" element={<BountyMarketplace userTier={userTier} hasFeatureAccess={hasFeatureAccess} user={user} />} />
          
          <Route path="/tutor" element={
            <VisualizationController>
              <TutorInterface userTier={userTier} hasFeatureAccess={hasFeatureAccess} user={user} onLogout={handleLogout} />
            </VisualizationController>
          } />
          
          {/* Neo Trainer - accessible to all tiers for low friction onboarding */}
          <Route 
            path="/neo-trainer" 
            element={
              <div style={{
                padding: '2rem',
                maxWidth: '800px',
                margin: '0 auto',
                minHeight: '600px'
              }}>
                <OneClickNeoTrainer />
              </div>
            } 
          />
          
          {/* Protected routes that require specific feature access */}
          <Route
            path="/parent"
            element={
              hasFeatureAccess('LEARNING_PORTFOLIO') ?
                <ParentDashboard userTier={userTier} hasFeatureAccess={hasFeatureAccess} /> :
                <Navigate to="/pricing" replace />
            }
          />

          {/* Family & Groups Management - uses ParentDashboard with Family tab active */}
          <Route
            path="/family"
            element={
              (userTier === 'home' || userTier === 'expert-family' || hasFeatureAccess('GROUP_MANAGEMENT')) ?
                <ParentDashboard
                  user={user}
                  onLogout={handleLogout}
                  userTier={userTier}
                  hasFeatureAccess={hasFeatureAccess}
                /> :
                <Navigate to="/pricing" replace />
            }
          />
          <Route 
            path="/trainer" 
            element={
              hasFeatureAccess('MARKETPLACE_ACCESS') ? 
                <SimpleNeoTrainer userTier={userTier} hasFeatureAccess={hasFeatureAccess} user={user} /> : 
                <Navigate to="/pricing" replace />
            } 
          />
          
          {/* Advanced Trainer - for power users who need complex controls */}
          <Route 
            path="/trainer/advanced" 
            element={
              hasFeatureAccess('MARKETPLACE_ACCESS') ? 
                <AITrainer userTier={userTier} hasFeatureAccess={hasFeatureAccess} /> : 
                <Navigate to="/pricing" replace />
            } 
          />
          
          {/* Friend Training - includes viral funnel for all tiers */}
          <Route 
            path="/friends" 
            element={<FriendTraining userTier={userTier} hasFeatureAccess={hasFeatureAccess} user={user} />} 
          />
          
          {/* Authenticated versions of research and pricing - keep users in app layout */}
          <Route path="/app/research" element={
            <Research onActivateVoice={() => window.location.href='/tutor'} />
          } />
          <Route path="/app/pricing" element={<PricingTiers userTier={userTier} currentUser={user} />} />
          <Route path="/app/accessibility" element={<Accessibility />} />
          
          {/* Public versions - redirect unauthenticated users to main domain */}
          <Route path="/research" element={
            <Research onActivateVoice={() => window.location.href='/tutor'} />
          } />
          <Route path="/pricing" element={<PricingTiers userTier={userTier} currentUser={user} />} />
          <Route path="/sponsor" element={<SponsorPage />} />
          <Route path="/upgrade" element={<UpgradePage user={user} userTier={userTier} />} />
          <Route path="/subscription" element={<SubscriptionManagement user={user} onBack={() => window.history.back()} />} />
          <Route path="/payment/success" element={<PaymentSuccess onRefreshUser={handleLoginSuccess} />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/join" element={<CodeEntryLanding />} />
          <Route path="/join/:invite_token" element={<FriendCollaborationInterface />} />
          <Route path="/class-join" element={<StudentJoinPage />} />
          <Route path="/welcome-helper" element={<WelcomeHelper />} />
          {/* Visualization tester route removed */}
          <Route path="/login" element={<Navigate to="/tutor" replace />} />
          <Route path="*" element={<Navigate to="/tutor" replace />} />
        </Routes>
      </main>

      {/* Flying Token Animation */}
      {tokenAnimation.show && (
        <div style={{
          position: 'fixed',
          left: tokenAnimation.fromX,
          top: tokenAnimation.fromY,
          fontSize: '24px',
          fontWeight: 'bold',
          color: '#F59E0B',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
          zIndex: 9999,
          pointerEvents: 'none',
          animation: 'flyToProfile 2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards',
          textShadow: '0 2px 4px rgba(0,0,0,0.3)'
        }}>
          +{tokenAnimation.amount} 🪙
        </div>
      )}

      {/* Token Wallet Modal */}
      <TokenWalletModal
        isOpen={showTokenWallet}
        onClose={() => setShowTokenWallet(false)}
        user={user}
        userTier={userTier}
        hasFeatureAccess={hasFeatureAccess}
      />

      {/* Hide footer on full-screen pages (TutorInterface handles its own footer) */}
      {location.pathname !== '/tutor' && (
        <footer className="app-footer">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
            <img
              src="/images/TAPPY-logo-black.png"
              alt="Tappy Tutor Logo"
              style={{
                height: '24px',
                width: 'auto',
                objectFit: 'contain',
                marginBottom: '0.5rem'
              }}
            />
            <p>&copy; 2025 Teach Tappy</p>
            <p style={{fontSize: '0.8rem', color: '#76909f'}}>Tappy is powered by <span style={{fontWeight: 'medium'}}>Morpheus RL</span> technology</p>
          </div>
        </footer>
      )}
    </div>
  );
}

// Add responsive styles for the header and token animations
const smallScreenStyles = `
@media (max-width: 768px) {
  .app-header {
    grid-template-columns: minmax(80px, auto) 1fr minmax(80px, auto) !important;
    padding: 0.75rem 1rem !important;
  }
}

@media (max-width: 580px) {
  .app-header {
    padding: 0.75rem 0.5rem !important;
    grid-template-columns: auto 1fr auto !important;
  }
  
  .app-header h1 {
    font-size: 1.2rem !important;
  }
  
  .app-header img {
    height: 28px !important;
  }
  
  .app-header nav {
    padding: 0.5rem 0 !important;
    gap: 0.25rem !important;
  }
}

/* Token Animation Keyframes */
@keyframes tokenBadgePulse {
  0% { 
    transform: scale(0.8); 
    opacity: 0; 
  }
  50% { 
    transform: scale(1.2); 
    opacity: 1; 
  }
  100% { 
    transform: scale(1); 
    opacity: 1; 
  }
}

@keyframes flyToProfile {
  0% { 
    transform: translateY(0) scale(1); 
    opacity: 1; 
  }
  70% { 
    opacity: 1; 
  }
  100% { 
    transform: translateX(calc(100vw - 120px)) translateY(-50px) scale(0.3); 
    opacity: 0; 
  }
}
`;

// Add the styles to the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = smallScreenStyles;
  document.head.appendChild(styleElement);
}

export default App;